import { createContext, useContext, useMemo, useState } from "react";
import { useAnalysisVersionValueContextHook } from "./AnalysisVersionContext";
import { ANALYSIS_VERSIONS } from "../variables/analysisVersions";

const StockCycleDatasValueContext = createContext();
const StockCycleDatasActionsContext = createContext();

const INITIAL_STOCK_CYCLE_DATAS_MAP = {
    [ANALYSIS_VERSIONS.V2]: null,
    [ANALYSIS_VERSIONS.V3]: null
}

/**
 * 이상치 판정 알고리즘 버전(A/B) 별로 분석 결과를 따로 보관한다.
 * 값 훅은 현재 활성화된 탭의 결과만 돌려주므로 화면단은 버전을 신경쓰지 않아도 된다.
 * AnalysisVersionContextProvider 하위에 위치해야 한다.
 */
export function StockCycleDatasContextProvider({ children }) {
    const analysisVersionValueContextHook = useAnalysisVersionValueContextHook();
    const analysisVersion = analysisVersionValueContextHook.analysisVersion;

    const [stockCycleDatasMap, setStockCycleDatasMap] = useState(INITIAL_STOCK_CYCLE_DATAS_MAP);

    const stockCycleDatasActions = useMemo(
        () => {
            return {
                onSet(newValues) {
                    setStockCycleDatasMap(prev => {
                        return {
                            ...prev,
                            [analysisVersion]: newValues
                        }
                    });
                },
                onSetByVersion(version, newValues) {
                    setStockCycleDatasMap(prev => {
                        return {
                            ...prev,
                            [version]: newValues
                        }
                    });
                },
                onClear() {
                    setStockCycleDatasMap(INITIAL_STOCK_CYCLE_DATAS_MAP);
                }
            }
        },
        [analysisVersion]
    )

    const value = useMemo(
        () => {
            return {
                stockCycleDatas: stockCycleDatasMap?.[analysisVersion] || null,
                stockCycleDatasMap: stockCycleDatasMap
            }
        },
        [stockCycleDatasMap, analysisVersion]
    )

    return (
        <>
            <StockCycleDatasActionsContext.Provider value={{ stockCycleDatas: stockCycleDatasActions }}>
                <StockCycleDatasValueContext.Provider value={value}>
                    {children}
                </StockCycleDatasValueContext.Provider>
            </StockCycleDatasActionsContext.Provider>
        </>
    );
}

export function useStockCycleDatasValueContextHook(props) {
    const value = useContext(StockCycleDatasValueContext);

    if (value === undefined) {
        throw new Error('useStockCycleDatasValueContextHook should be used within StockCycleDatasValueContext');
    }

    return {
        stockCycleDatas: value.stockCycleDatas,
        stockCycleDatasMap: value.stockCycleDatasMap
    };
}

export function useStockCycleDatasActionsContextHook(props) {
    const value = useContext(StockCycleDatasActionsContext);

    if (value === undefined) {
        throw new Error('useStockCycleDatasActionsContextHook should be used within StockCycleDatasActionsContext');
    }

    return {
        stockCycleDatas: {
            onSet: value.stockCycleDatas.onSet,
            onSetByVersion: value.stockCycleDatas.onSetByVersion,
            onClear: value.stockCycleDatas.onClear
        },
    };
}
